import { useContext, useEffect } from 'react'
import { useIntegrationAssignment } from '@global/hooks'
import {
  TextField,
  SyncSchedule,
  IntegrationTitle,
  IntegrationFilters,
  FieldMappingManager,
  DropDown,
  DataSelection,
  AccountSelection,
  MessageField,
  CheckboxField,
  IntegrationFiltersManager,
} from './integration-form-fields'
import {
  getAccountSelectionConfig,
  getCheckboxFieldConfig,
  getCustomFieldConfig,
  getDataSelectionFieldConfig,
  getDropDownFieldConfig,
  getFieldMappingFieldConfig,
  getFilterFieldConfig,
  getIntegrationFiltersFieldConfig,
  getMessageFieldConfig,
  getSyncScheduleFieldConfig,
  getTextFieldConfig,
  IntegrationFormField,
} from './integration-form-types'
import { templateCopyContext } from '../../../context'
import { t } from '@lingui/core/macro'

interface IntegrationFormBuilderProps {
  integrationType: string
  integrationAssignment: any
  field: IntegrationFormField
  assignmentMetaDataType?: string
}

export const IntegrationFormFieldBuilder = ({
  integrationAssignment,
  integrationType,
  field,
  assignmentMetaDataType,
}: IntegrationFormBuilderProps) => {
  const { onChange } = useContext<any>(templateCopyContext)
  const { onIntegrationMetadataChange } = useIntegrationAssignment({
    integrationAssignment,
    onChange,
  })

  // Update Integration metadata in Redux store state, if field is
  // is a data_selection type.
  useEffect(() => {
    if (field.field !== 'data_selection') return
    const dataFieldConfig = getDataSelectionFieldConfig(field.config)
    if (!dataFieldConfig?.defaultValue) return
    if (integrationAssignment.metadata[dataFieldConfig.metadataKey]) return

    onIntegrationMetadataChange(dataFieldConfig.defaultValue, dataFieldConfig.metadataKey)
  }, [field.config, field.field])

  if (field.config.isHidden) {
    return <div />
  }

  if (field.field === 'integration_name') {
    return (
      <IntegrationTitle
        integrationTitle={integrationAssignment.name}
        integrationAssignment={integrationAssignment}
      />
    )
  }

  if (field.field === 'account_selection') {
    const accountSelectionConfig = getAccountSelectionConfig(field.config)

    if (!accountSelectionConfig) return <div />

    return (
      <AccountSelection
        integrationAssignment={integrationAssignment}
        selectedAccount={integrationAssignment.connector_id}
        resetOnChange={accountSelectionConfig.resetOnAccountChange}
        dataType={accountSelectionConfig.dataType}
        disabled={accountSelectionConfig.disabled}
      />
    )
  }

  if (field.field === 'sync_schedule') {
    const syncScheduleConfig = getSyncScheduleFieldConfig(field.config)

    if (!syncScheduleConfig) return <div />

    return (
      <SyncSchedule
        integrationOptions={[{ label: t`Every 8 Hours`, value: '8hr' }]}
        syncSelection="8hr"
        disabled={syncScheduleConfig.disabled}
        integrationAssignment={integrationAssignment}
      />
    )
  }

  if (field.field === 'field_mapping') {
    const fieldMappingConfig = getFieldMappingFieldConfig(field.config)

    if (!fieldMappingConfig) return <div />

    return (
      <FieldMappingManager
        integrationType={integrationType}
        integrationAssignment={integrationAssignment}
        integrationTitle={fieldMappingConfig.integrationTitle}
        mappingType={fieldMappingConfig.mappingType}
        integrationFieldsFilter={fieldMappingConfig.integrationFieldsFilter}
        mappingsDisabled={fieldMappingConfig.mappingsDisabled}
        defaultMappings={fieldMappingConfig.defaultMappings}
        dependentMappings={fieldMappingConfig.dependentMappings}
        dataType={fieldMappingConfig.dataType}
        assignmentMetaDataType={assignmentMetaDataType}
        includeCreativeClassification={fieldMappingConfig.includeCreativeClassification}
      />
    )
  }

  if (field.field === 'filters') {
    const filterFieldConfig = getFilterFieldConfig(field.config)

    if (!filterFieldConfig) return <div />

    return (
      <IntegrationFilters
        integrationAssignment={integrationAssignment}
        filters={filterFieldConfig ? filterFieldConfig.filters : []}
      />
    )
  }

  if (field.field === 'integration_filters') {
    const integrationFilterFieldConfig = getIntegrationFiltersFieldConfig(field.config)

    return (
      <IntegrationFiltersManager
        integrationAssignment={integrationAssignment}
        dataTypeFilter={integrationFilterFieldConfig?.dataTypeFilter}
        requiredDateFilterDefaults={integrationFilterFieldConfig?.requiredDateFilterDefaults}
        previousYearsRestriction={integrationFilterFieldConfig?.previousYearsRestriction}
        integrationType={integrationType}
      />
    )
  }

  if (field.field === 'data_selection') {
    const dataFieldConfig = getDataSelectionFieldConfig(field.config)

    if (!dataFieldConfig) return <div />

    return (
      <DataSelection
        integrationAssignment={integrationAssignment}
        options={dataFieldConfig ? dataFieldConfig.dataOptions : []}
        metadataKey={dataFieldConfig ? dataFieldConfig.metadataKey : ''}
        selectedDataType={
          integrationAssignment.metadata[dataFieldConfig.metadataKey] ||
          dataFieldConfig.defaultValue
        }
        resetIntegrationOnChange={dataFieldConfig.resetIntegrationOnChange}
        filtersToReset={dataFieldConfig.filtersToReset}
        disabled={dataFieldConfig.disabled}
      />
    )
  }

  if (field.field === 'custom') {
    const customFieldConfig = getCustomFieldConfig(field.config)

    if (!customFieldConfig) return <div />

    return customFieldConfig.customField
  }

  if (field.field === 'drop_down') {
    const dropDownFieldConfig = getDropDownFieldConfig(field.config)

    if (!dropDownFieldConfig) return <div />

    return (
      <DropDown
        title={dropDownFieldConfig.title}
        desription={dropDownFieldConfig.description}
        metadataKey={dropDownFieldConfig.metadataKey}
        selected={dropDownFieldConfig.selected}
        options={dropDownFieldConfig.options}
        label={dropDownFieldConfig.label}
        integrationAssignment={integrationAssignment}
        waitingOnDependancy={dropDownFieldConfig.waitingOnDependancy}
        dependancyTitle={dropDownFieldConfig.dependancyTitle}
        isLoading={dropDownFieldConfig.isLoading}
        isMulti={dropDownFieldConfig.isMulti}
      />
    )
  }

  if (field.field === 'text_field') {
    const textFieldConfig = getTextFieldConfig(field.config)

    if (!textFieldConfig) return <div />

    return (
      <TextField
        title={textFieldConfig.title}
        label={textFieldConfig.label}
        integrationAssignment={integrationAssignment}
        textValue={textFieldConfig.textValue}
        desription={textFieldConfig.description}
        metadataKey={textFieldConfig.metadataKey}
      />
    )
  }

  if (field.field === 'message_field') {
    const messageFieldConfig = getMessageFieldConfig(field.config)

    if (!messageFieldConfig) return <div />

    return <MessageField {...messageFieldConfig} />
  }

  if (field.field === 'checkbox_field') {
    const checkboxFieldConfig = getCheckboxFieldConfig(field.config)

    if (!checkboxFieldConfig) return <div />

    return (
      <CheckboxField
        integrationAssignment={integrationAssignment}
        label={checkboxFieldConfig.label}
        checked={checkboxFieldConfig.checked}
        metadataKey={checkboxFieldConfig.metadataKey}
      />
    )
  }

  return <div />
}
